import { VenueActivityType, MapFilterState } from '@ivy-monorepo/shared'
import { MapFilterConfigItem, MapFilters } from '../../types/liveMap'

/**
 * Filter config plumbing for the map filter card. The server (/auth/me
 * `mapFilters`) drives the UI; when absent we fall back to the same hardcoded
 * list the mobile app uses.
 */

/** Hardcoded fallback, mirroring mobile `MapFilterConfigService.fallbackItems`. */
export const FALLBACK_MAP_FILTERS: MapFilterConfigItem[] = [
  {
    key: VenueActivityType.CheckedIns,
    label: 'Live check ins',
    position: 0,
    color: '#AA0100',
    state: MapFilterState.Locked,
  },
  {
    key: VenueActivityType.Events,
    label: 'RSVPs',
    position: 1,
    color: '#2270DD',
    state: MapFilterState.Locked,
  },
  {
    key: VenueActivityType.VisitPlans,
    label: 'Visit plans',
    position: 2,
    color: '#2270DD',
    state: MapFilterState.Hidden,
  },
  {
    key: VenueActivityType.Breadcrumbs,
    label: 'Breadcrumbs',
    position: 3,
    color: '#E5A200',
    state: MapFilterState.Init,
  },
  {
    key: VenueActivityType.RecentCheckins,
    label: 'Recent Check ins',
    position: 4,
    color: '#7A42F4',
    state: MapFilterState.Default,
  },
  {
    key: VenueActivityType.Views,
    label: 'Recent Views',
    position: 5,
    color: '#8C8C8C',
    state: MapFilterState.Default,
  },
  {
    key: VenueActivityType.Stickers,
    label: 'Stickers',
    position: 6,
    color: '#9747FF',
    state: MapFilterState.Default,
  },
]

/** Maps an activity key to the corresponding MapFilters boolean field. */
const KEY_TO_FIELD: Partial<Record<VenueActivityType, keyof MapFilters>> = {
  [VenueActivityType.CheckedIns]: 'checkIns',
  [VenueActivityType.Events]: 'events',
  [VenueActivityType.VisitPlans]: 'visitPlans',
  [VenueActivityType.Breadcrumbs]: 'breadcrumbs',
  [VenueActivityType.RecentCheckins]: 'recentCheckIns',
  [VenueActivityType.Views]: 'recentViews',
  [VenueActivityType.Stickers]: 'stickers',
}

export function fieldForKey(key: VenueActivityType): keyof MapFilters | undefined {
  return KEY_TO_FIELD[key]
}

/** Locked filters are always on and cannot be toggled (premium-gated). */
export function isLocked(item: MapFilterConfigItem): boolean {
  return item.state === MapFilterState.Locked
}

/** Hidden filters are not rendered in the UI. */
export function isHidden(item: MapFilterConfigItem): boolean {
  return item.state === MapFilterState.Hidden
}

/** Filters that start in the "on" position by default. */
export function isOnByDefault(item: MapFilterConfigItem): boolean {
  return item.state === MapFilterState.Locked || item.state === MapFilterState.Init
}

/**
 * Resolve which filter config to use: server config when present and non-empty,
 * otherwise the hardcoded fallback. Returns items sorted by position.
 */
export function resolveFilterConfig(
  serverItems?: MapFilterConfigItem[] | null
): MapFilterConfigItem[] {
  const items = serverItems && serverItems.length > 0 ? serverItems : FALLBACK_MAP_FILTERS
  return [...items].sort((a, b) => a.position - b.position)
}

/** Visible (non-hidden) rows, sorted by position. */
export function visibleFilters(items: MapFilterConfigItem[]): MapFilterConfigItem[] {
  return resolveFilterConfig(items).filter(i => !isHidden(i))
}

/** Default toggle state, with mobile's defaults as the baseline. */
const DEFAULT_FILTERS: MapFilters = {
  checkIns: true,
  events: true,
  visitPlans: true, // subordinate to events; hidden in UI but defaults on
  breadcrumbs: true,
  recentCheckIns: false,
  recentViews: false,
  stickers: false,
}

/** Builds the initial MapFilters from a (resolved) config list. */
export function initialFiltersFromConfig(items: MapFilterConfigItem[]): MapFilters {
  const filters: MapFilters = { ...DEFAULT_FILTERS }
  for (const item of items) {
    const field = fieldForKey(item.key)
    if (!field) continue
    // Hidden rows keep their baseline default (e.g. visit plans stay on).
    if (isHidden(item)) continue
    filters[field] = isOnByDefault(item)
  }
  return filters
}
